import React from 'react';
import { isPartnerTenant } from 'utils/tenantUtils';

/**
 * Tenant selector dropdown component
 */
export const TenantSelector = ({
  formData,
  tenants,
  loadingTenants,
  dropdownOpen,
  setDropdownOpen,
  setFormData,
  setIsSelectedTenantPartner,
  setIsDevTenant,
  loadCostPools,
  loadPrices,
  handleRetryTenants,
  mode,
  disabledTenantIds = []
}) => {
  return (
    <div className="form-group">
      <label htmlFor="tenant_id">Tenant *</label>
      <div className={loadingTenants ? 'dropdown-loading' : ''}>
        <div className="custom-dropdown">
          <div className="dropdown-trigger" onClick={() => setDropdownOpen(!dropdownOpen)}>
            <span className="dropdown-text">
              {formData.tenant_id ? 
                (() => {
                  const selectedTenant = tenants.find(t => t.id === formData.tenant_id);
                  return selectedTenant ? selectedTenant.name || selectedTenant.id : 'Select a tenant...';
                })() : 
                'Select a tenant...'
              }
            </span>
            <span className="dropdown-arrow">▼</span>
          </div>
          {dropdownOpen && (
            <div className="dropdown-menu">
              <div 
                className="dropdown-option"
                onClick={() => {
                  setFormData(prev => ({ ...prev, tenant_id: '' }));
                  setDropdownOpen(false);
                }}
              >
                <span>Select a tenant...</span>
              </div>
              {tenants.length === 0 && !loadingTenants ? (
                <div className="dropdown-option error-option">
                  <span>No tenants found</span>
                  <button 
                    type="button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryTenants();
                    }}
                    className="retry-button-small"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                tenants.map(tenant => {
                  const isDevTenant = tenant.is_dev_tenant || false;
                  const isPartnerTenantFlag = isPartnerTenant(tenant);
                  const tenantTypeLabel = isDevTenant ? 'DEV' : isPartnerTenantFlag ? 'PARTNER' : 'STANDARD';
                  const tenantTypeColor = isDevTenant ? '#f59e0b' : isPartnerTenantFlag ? '#8b5cf6' : '#6b7280';
                  const isDisabled = disabledTenantIds.includes(tenant.id);
                  
                  return (
                    <div 
                      key={tenant.id}
                      className={`dropdown-option${isDisabled ? ' dropdown-option--disabled' : ''}`}
                      title={isDisabled ? 'This tenant already has a resale resource (one per tenant)' : undefined}
                      onClick={() => {
                        if (isDisabled) return;
                        const selectedTenant = tenants.find(t => t.id === tenant.id);
                        const isPartner = selectedTenant ? isPartnerTenant(selectedTenant) : false;
                        const isDev = selectedTenant?.is_dev_tenant || false;
                        
                        console.log('🎯 Tenant selected:', {
                          tenant_id: tenant.id,
                          tenant_name: tenant.name,
                          isPartner,
                          isDev,
                          tenant_data: {
                            tenant_type: selectedTenant?.tenant_type,
                            tenant_category: selectedTenant?.tenant_category,
                            is_partner: selectedTenant?.is_partner,
                            partner: selectedTenant?.partner,
                            metadata: selectedTenant?.metadata
                          }
                        });
                        
                        setFormData(prev => ({ ...prev, tenant_id: tenant.id }));
                        setIsSelectedTenantPartner(isPartner);
                        setIsDevTenant(isDev);
                        // Load cost pools using the callback from BasicsTab which calculates includeAll correctly
                        // This ensures cost pools are loaded with the right filter based on dev resource state and free prices
                        if (loadCostPools) {
                          loadCostPools(tenant.id);
                        }
                        
                        // Reload prices if product is already selected - pass explicit tenant ID to avoid race condition
                        // This ensures prices are loaded with the correct tenant context
                        if (formData.configuration?.product_id && mode === 'create' && loadPrices) {
                          loadPrices(formData.configuration.product_id, tenant.id);
                        }
                        
                        setDropdownOpen(false);
                      }}
                    >
                      <span className="tenant-name">{tenant.name || tenant.id}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {isDisabled && (
                          <span className="tenant-badge" style={{ backgroundColor: '#94a3b8', fontSize: '0.65rem' }}>
                            Already has resale
                          </span>
                        )}
                        <span className="tenant-badge" style={{ backgroundColor: tenantTypeColor }}>
                          {tenantTypeLabel}
                        </span>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      {loadingTenants && (
        <div className="loading-text">
          <div className="loading-spinner"></div>
          Loading tenants...
        </div>
      )}
      {!loadingTenants && tenants.length === 0 && (
        <div className="error-text">
          <span>Failed to load tenants</span>
          <button 
            type="button" 
            onClick={handleRetryTenants}
            className="retry-button-small"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};


